/**
 * ChatView — main chat container with empty state, message list, and input.
 *
 * Supports drag-and-drop file attachment — dragging files over the chat area
 * shows a drop zone overlay, and dropping adds them as attached files.
 */

import { useState, useRef, useCallback, useSyncExternalStore } from "react";
import { useChatStore } from "@/stores/chatStore";
import { authedUpload } from "@/api/client";
import type { AttachedFile } from "@/stores/chatStore";
import { MessageList } from "./MessageList";
import { ChatInput } from "./ChatInput";
import { ModelSelector } from "./ModelSelector";
import { TokenCounter } from "./TokenCounter";
import { CostCounter } from "./CostCounter";
import { SessionExpiredModal } from "./SessionExpiredModal";

// Subscribe to browser online/offline status without re-render flicker.
const subscribeOnline = (cb: () => void) => {
  window.addEventListener("online", cb);
  window.addEventListener("offline", cb);
  return () => {
    window.removeEventListener("online", cb);
    window.removeEventListener("offline", cb);
  };
};
const getOnlineSnapshot = () => navigator.onLine;

/**
 * Shimmer placeholder shown while history is loading after a conversation
 * switch. Prevents the empty/welcome state from flashing during the gap
 * between `openConversation` clearing `messages` and the detail fetch
 * resolving.
 */
function MessageSkeleton() {
  return (
    <div className="h-full overflow-hidden px-4 py-6">
      <div className="max-w-3xl mx-auto space-y-4">
        <div className="flex justify-end">
          <div className="h-10 w-2/5 shimmer rounded-2xl" />
        </div>
        <div className="space-y-2">
          <div className="h-3 w-3/4 shimmer rounded" />
          <div className="h-3 w-2/3 shimmer rounded" />
          <div className="h-3 w-1/2 shimmer rounded" />
        </div>
        <div className="flex justify-end">
          <div className="h-8 w-1/3 shimmer rounded-2xl" />
        </div>
        <div className="space-y-2">
          <div className="h-3 w-4/5 shimmer rounded" />
          <div className="h-3 w-3/5 shimmer rounded" />
        </div>
      </div>
    </div>
  );
}

export function ChatView() {
  const activeConversationId = useChatStore((s) => s.activeConversationId);
  const viewingAsManager = useChatStore((s) => s.viewingAsManager);
  const wsStatus = useChatStore((s) => s.wsStatus);
  const hasMessages = useChatStore((s) => s.messages.length > 0);
  const historyLoaded = useChatStore((s) => s._historyLoaded);
  const addFiles = useChatStore((s) => s.addFiles);
  const isOnline = useSyncExternalStore(subscribeOnline, getOnlineSnapshot);

  // Track drag depth so nested elements don't flicker the overlay
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounter = useRef(0);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.types.includes("Files")) {
      setIsDragOver(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragOver(false);

    if (e.dataTransfer.files.length === 0) return;

    // Browsers hide real filesystem paths for security. Upload dropped files
    // to a temp directory on the backend, which returns absolute paths the
    // agent can read directly. This avoids opening a redundant file dialog.
    const droppedFiles = Array.from(e.dataTransfer.files);
    const attached: AttachedFile[] = [];

    for (const file of droppedFiles) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        // Land the drop in this conversation's workspace (bounded + synced +
        // sandbox-seeded) rather than the legacy shared drops dir.
        const dropUrl = activeConversationId
          ? `/api/files/drop?conversation_id=${encodeURIComponent(activeConversationId)}`
          : "/api/files/drop";
        const res = await authedUpload(dropUrl, formData);
        if (res.ok) {
          const data = await res.json();
          if (data.path) {
            attached.push({ name: file.name, path: data.path });
          }
        }
      } catch (err) {
        console.error("Drop upload failed:", err);
      }
    }

    if (attached.length > 0) {
      addFiles(attached);
    }
  }, [addFiles]);

  // No conversation selected — only happens briefly on the first
  // render before AppShell's URL-sync effect picks up the param and
  // calls openConversation. Show a quiet shimmer so the screen never
  // flashes a dead-end "start a conversation" call to action; the
  // home page (``/``) is now the canonical entry point for that
  // flow.
  if (!activeConversationId) {
    return <MessageSkeleton />;
  }

  return (
    <div
      className="h-full flex flex-col relative bg-surface"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drag-and-drop overlay */}
      {isDragOver && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-jove-10/90 backdrop-blur-sm border-2 border-dashed border-jove rounded-xl pointer-events-none">
          <div className="flex flex-col items-center gap-3">
            <div className="w-14 h-14 rounded-2xl bg-jove-30 flex items-center justify-center">
              <svg className="w-7 h-7 text-jove animate-bounce" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
              </svg>
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold text-jove">Drop files here</p>
              {/* ``code-ink`` for the same reason as the attachment chips — fixed
                  jove-200 goes near-invisible on this tint in dark mode. */}
              <p className="text-xs text-code-ink mt-0.5">Files will be attached to your message</p>
            </div>
          </div>
        </div>
      )}

      {/* Connection status bars — offline takes priority over WS status */}
      {!isOnline && (
        <div className="px-4 py-1.5 text-xs text-center bg-hydra-50 text-hydra flex items-center justify-center gap-2 border-b border-hydra/20">
          <span className="w-1.5 h-1.5 rounded-full bg-hydra" />
          You are offline — check your internet connection
        </div>
      )}
      {isOnline && wsStatus === "connecting" && (
        <div className="px-4 py-1.5 text-xs text-center bg-gold/10 text-gold flex items-center justify-center gap-2 border-b border-gold/20">
          <span className="w-1.5 h-1.5 rounded-full bg-gold animate-[dotPulse_1s_ease-in-out_infinite]" />
          Connecting to agent...
        </div>
      )}
      {isOnline && wsStatus === "disconnected" && hasMessages && (
        <div className="px-4 py-1.5 text-xs text-center bg-space-20 text-space-50 flex items-center justify-center gap-2 border-b border-space-30">
          <span className="w-1.5 h-1.5 rounded-full bg-space-40" />
          Disconnected — reconnecting...
        </div>
      )}
      {wsStatus === "session_expired" && (
        <SessionExpiredModal />
      )}
      {/* ADR-0009: persistent notice while viewing a team member's
          conversation — cross-user visibility is read-only and audited. */}
      {viewingAsManager && (
        <div className="px-4 py-1.5 text-xs text-center bg-jove-10 text-jove flex items-center justify-center gap-2 border-b border-jove-30">
          <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <span>
            Viewing <span className="font-semibold">{viewingAsManager.ownerName}</span>'s
            conversation (read-only) — access is audited.
          </span>
        </div>
      )}
      {isOnline && wsStatus === "error" && (
        <div className="px-4 py-1.5 text-xs text-center bg-hydra-50 text-hydra flex items-center justify-center gap-2 border-b border-hydra/20">
          <span className="w-1.5 h-1.5 rounded-full bg-hydra animate-[dotPulse_1s_ease-in-out_infinite]" />
          Connection error — retrying automatically...
        </div>
      )}

      {/* Messages area
       *
       * Three states:
       *  - History still loading after a conversation switch → shimmer.
       *  - History loaded with content → MessageList (the agent's plan
       *    lives in the right panel's "Plan" tab, not above the messages).
       *  - History loaded but empty → welcome state with prompt suggestions.
       *
       * The welcome state must gate on `historyLoaded`; otherwise the empty
       * `messages` array set by `openConversation` causes it to flash for the
       * duration of the detail fetch.
       */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {!historyLoaded ? (
          <MessageSkeleton />
        ) : hasMessages ? (
          /* The agent's live plan now lives in the right panel's "Plan"
             tab (see RightPanel) rather than pinned above the messages,
             keeping the conversation column uncluttered. */
          <div className="flex-1 overflow-hidden">
            <MessageList />
          </div>
        ) : (
          /* Empty conversation — no welcome state any more. The
             chat input below is the only affordance the user needs;
             they start typing and the agent answers. New chats are
             expected to come pre-seeded via the home page's input,
             so this branch only fires for rare cases like opening
             an old conversation that never received a message. */
          <div className="flex-1" />
        )}
      </div>

      {/* Input — replaced on a team member's conversation (ADR-0009) by a
          read-only footer: the same model / token / cost widgets the owner
          sees in the composer, so an admin gets at-a-glance clarity on what
          model this chat ran and its token + dollar consumption — but no
          composer (cross-user visibility is read-only; the backend refuses
          drive frames anyway, this keeps the UI honest about it). The
          widgets are inert: ModelSelector is read-only, and Token/Cost
          counters only display (a click opens their read-only breakdown). */}
      {viewingAsManager ? (
        <div className="border-t border-space-30 select-none">
          <div className="flex flex-wrap items-center gap-2 px-4 py-2.5">
            <ModelSelector readOnly persistSelection={false} lockWhileRunning={false} />
            <TokenCounter />
            <CostCounter />
            <span className="ml-auto text-xs text-space-40">
              Read-only — only {viewingAsManager.ownerName} can send messages here.
            </span>
          </div>
        </div>
      ) : (
        <ChatInput />
      )}
    </div>
  );
}
